import React, { useEffect, useMemo } from "react";

import { ChevronDown, ChevronUp, Pencil, Plus, Save, Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import useCommissionCatalog from "@/hooks/useCommissionCatalog";
import { getQuoteFieldLabel, isCompanyQuoteField, isIssueAgeQuoteField, isProductQuoteField } from "@/lib/quoteFieldUtils";

import SectionCard from "@/pages/appointmentDetails/SectionCard";

export default function AppointmentQuotesSection({
  accentIconButtonClass,
  saveIconButtonClass,
  quotesEditMode,
  quotesDirty,
  isQuotesSaving,
  setQuoteTemplateOpen,
  setQuotesEditMode,
  setQuotesDirty,
  handleSaveManualQuotes,

  quoteProspects,
  hiddenQuotePanels,
  openQuoteProspectId,
  setOpenQuoteProspectId,

  soldPayloads,
  quotes,
  quoteProspectLeadMap,
  buildSoldPrefill,
  setSoldContext,
  setSoldOpen,
  handleDeleteManualQuote,
  handleHideRunQuote,

  updateManualQuoteValue,
}) {
  const formatQuoteDate = (value) => {
    if (!value) return "—";
    const s = String(value);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[2]}/${m[3]}/${m[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  };
  const {
    companyOptions,
    ensureProducts,
    getCompanyIdByName,
    getProductOptionsByCompanyName,
  } = useCommissionCatalog({ enabled: true, filterByProfiles: true });

  const [commissionProfiles, setCommissionProfiles] = React.useState([]);
  const [profilesLoading, setProfilesLoading] = React.useState(false);

  useEffect(() => {
    const loadProfiles = async () => {
      setProfilesLoading(true);
      try {
        const res = await api.get("/commission/profiles");
        setCommissionProfiles(Array.isArray(res.data) ? res.data : []);
      } catch {
        setCommissionProfiles([]);
      } finally {
        setProfilesLoading(false);
      }
    };
    loadProfiles();
  }, []);

  const getIssueAgeOptions = React.useCallback(
    (companyName, productName) => {
      const company = String(companyName || "").trim().toLowerCase();
      const product = String(productName || "").trim().toLowerCase();
      if (!company || !product) return [];
      const matches = (commissionProfiles || []).filter(
        (profile) =>
          String(profile?.company_name || "").trim().toLowerCase() === company &&
          String(profile?.product_name || "").trim().toLowerCase() === product
      );
      const unique = Array.from(
        new Set(
          matches
            .map((profile) => profile.issue_age)
            .filter((age) => age !== undefined && age !== null)
            .map((age) => String(age))
        )
      );
      return unique.sort((a, b) => {
        const na = Number.parseFloat(a);
        const nb = Number.parseFloat(b);
        if (Number.isNaN(na) || Number.isNaN(nb)) return a.localeCompare(b);
        return na - nb;
      });
    },
    [commissionProfiles]
  );

  const companySelections = useMemo(() => {
    const names = new Set();
    (quoteProspects || []).forEach((panel) => {
      const rows = Array.isArray(panel?.quote_rows) ? panel.quote_rows : [];
      const companyRow = rows.find((row) => isCompanyQuoteField(getQuoteFieldLabel(row)));
      if (!companyRow?.options) return;
      Object.values(companyRow.options || {}).forEach((val) => {
        if (val) names.add(String(val));
      });
    });
    return Array.from(names);
  }, [quoteProspects]);

  useEffect(() => {
    companySelections.forEach((name) => {
      const id = getCompanyIdByName(name);
      if (id) ensureProducts(id);
    });
  }, [companySelections, ensureProducts, getCompanyIdByName]);

  return (
    <SectionCard
      title="Quotes"
      right={
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            className={accentIconButtonClass}
            onClick={() => setQuoteTemplateOpen(true)}
            data-testid="appointment-details-quotes-add-button"
          >
            <Plus className="h-4 w-4" />
          </Button>
          {quotesEditMode && quotesDirty ? (
            <Button
              type="button"
              variant="secondary"
              className={saveIconButtonClass}
              onClick={handleSaveManualQuotes}
              disabled={isQuotesSaving}
              data-testid="appointment-details-quotes-save-button"
            >
              <Save className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              className={accentIconButtonClass}
              onClick={() => {
                if (quotesEditMode) {
                  setQuotesEditMode(false);
                  setQuotesDirty(false);
                  return;
                }
                setQuotesEditMode(true);
                setQuotesDirty(false);
              }}
              data-testid="appointment-details-quotes-edit-button"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-3" data-testid="appointment-quotes-section">
        {!quoteProspects.length ? (
          <div className="text-xs text-slate-200/60" data-testid="appointment-quotes-empty">
            No quotes found.
          </div>
        ) : null}

        {quoteProspects.length ? (
          <div className="space-y-4">
            {quoteProspects.map((p, idx) => {
              const panelId = p._panel_kind === "manual" ? `manual:${p.id}` : `run:${p.prospect_id || p.id || idx}`;
              if (hiddenQuotePanels.includes(panelId)) {
                return null;
              }
              const isOpen = openQuoteProspectId === panelId;
              const rows = Array.isArray(p.quote_rows) ? p.quote_rows : [];
              const quoteDate = p._panel_kind === "manual" ? p.created_at : p.run_at;
              const companyRow = rows.find((row) => isCompanyQuoteField(getQuoteFieldLabel(row)));
              const productRow = rows.find((row) => isProductQuoteField(getQuoteFieldLabel(row)));
              const soldPayloadsForPanel = soldPayloads.filter((payload) => payload?.quote_ref?.panel_id === panelId);
              const soldOptionSet = new Set(
                soldPayloadsForPanel.map((payload) => Number(payload?.sold_option_index)).filter((val) => Number.isFinite(val))
              );
              const isSoldQuote = soldOptionSet.size > 0;

              return (
                <div key={panelId} className="space-y-3" data-testid={`appointment-quotes-prospect-${panelId}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenQuoteProspectId(isOpen ? null : panelId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setOpenQuoteProspectId(isOpen ? null : panelId);
                      }
                    }}
                    className="flex w-full items-center justify-between rounded-xl bg-white/5 px-4 py-3 pl-3 text-left ring-1 ring-white/10 border-l-4 border-indigo-500/60 hover:bg-white/10"
                    data-testid={`appointment-quotes-toggle-${panelId}`}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-slate-100">{p.prospect_name || "Prospect"}</span>
                      <span className="text-xs text-slate-200/70">
                        {p.playbook_name || quotes?.runs?.[0]?.script_name || "Playbook"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {isSoldQuote ? (
                        <span
                          className="rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-semibold text-emerald-100 ring-1 ring-emerald-400/30"
                          data-testid={`appointment-quotes-sold-badge-${panelId}`}
                        >
                          Sold
                        </span>
                      ) : null}

                      {quotesEditMode ? (
                        <button
                          type="button"
                          className="rounded-full bg-rose-500/15 px-2 py-1 text-xs font-semibold text-rose-100 ring-1 ring-rose-400/30 hover:bg-rose-500/25"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (p._panel_kind === "manual") {
                              handleDeleteManualQuote(p.id, panelId);
                            } else {
                              handleHideRunQuote(panelId);
                            }
                          }}
                          data-testid={`appointment-quotes-delete-button-${panelId}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : null}

                      {!isSoldQuote ? (
                        <button
                          type="button"
                          className="rounded-full bg-indigo-500/90 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
                          onClick={(event) => {
                            event.stopPropagation();
                            const prospectKey = p.prospect_id || p.id || null;
                            const leadId = p.lead_id || (prospectKey ? quoteProspectLeadMap.get(prospectKey) : null) || null;
                            const quoteRef = {
                              panel_id: panelId,
                              panel_kind: p._panel_kind,
                              manual_quote_id: p._panel_kind === "manual" ? p.id : null,
                              prospect_id: prospectKey,
                              lead_id: leadId,
                              prospect_name: p.prospect_name || "Prospect",
                              playbook_name: p.playbook_name || "",
                              quote_rows: Array.isArray(p?.quote_rows) ? p.quote_rows : [],
                            };
                            setSoldContext({ prefill: buildSoldPrefill(p), quoteRef });
                            setSoldOpen(true);
                          }}
                          data-testid={`appointment-quotes-sold-button-${panelId}`}
                        >
                          Sold
                        </button>
                      ) : null}

                      {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-slate-200/70" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-slate-200/70" />
                      )}
                    </div>
                  </div>

                  {isOpen ? (
                    <div className="rounded-xl border border-white/10 bg-white/5 px-4 pb-4 pt-3">
                      <div
                        className="text-xs text-slate-200/70"
                        data-testid={`appointment-quote-date-${panelId}`}
                      >
                        Quote Date: {formatQuoteDate(quoteDate)}
                      </div>
                      <Table className="mt-3">
                        <TableHeader>
                          <TableRow className="border-white/10">
                            <TableHead className="text-slate-200/70">Quote Field</TableHead>
                            {[1, 2, 3].map((opt) => {
                              const isSoldOpt = soldOptionSet.has(opt);
                              return (
                                <TableHead
                                  key={opt}
                                  className={`text-slate-200/70 ${isSoldOpt ? "bg-emerald-500/15 text-emerald-100" : ""}`}
                                  data-testid={`appointment-quotes-option${opt}-header-${panelId}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span>Option {opt}</span>
                                    {isSoldOpt ? (
                                      <span
                                        className="rounded-full bg-emerald-500/30 px-2 py-0.5 text-[10px] uppercase text-emerald-100 ring-1 ring-emerald-400/30"
                                        data-testid={`appointment-quotes-option${opt}-sold-label-${panelId}`}
                                      >
                                        Sold
                                      </span>
                                    ) : null}
                                  </div>
                                </TableHead>
                              );
                            })}
                          </TableRow>
                        </TableHeader>

                        <TableBody>
                          {rows.map((r) => {
                            const rowId = r.quote_field_id || r.id || r.label;
                            const rowLabel = getQuoteFieldLabel(r);
                            const isManual = Boolean(p.is_manual);
                            const fieldType = r.field_type || "text";
                            const isCompany = isCompanyQuoteField(rowLabel);
                            const isProduct = isProductQuoteField(rowLabel);
                            const isIssueAge = isIssueAgeQuoteField(rowLabel);
                            const resolvedFieldType = isCompany || isProduct || isIssueAge ? "dropdown" : fieldType;
                            const inputType = resolvedFieldType === "number" ? "number" : "text";

                            return (
                              <TableRow
                                key={rowId}
                                className="border-white/10"
                                data-testid={`appointment-quotes-row-${p.prospect_id}-${rowId}`}
                              >
                                <TableCell
                                  className="text-sm text-slate-100"
                                  data-testid={`appointment-quotes-field-${p.prospect_id}-${rowId}`}
                                >
                                  {r.label || r.quote_field_id || rowId}
                                </TableCell>

                                {[1, 2, 3].map((opt) => {
                                  const isSoldOpt = soldOptionSet.has(opt);
                                  return (
                                    <TableCell
                                      key={opt}
                                      className={`text-sm text-slate-200/80 ${
                                        isSoldOpt ? "bg-emerald-500/10 text-emerald-100" : ""
                                      }`}
                                      data-testid={`appointment-quotes-option${opt}-${p.prospect_id}-${rowId}`}
                                    >
                                      {isManual ? (
                                        quotesEditMode ? (
                                          resolvedFieldType === "dropdown" ? (
                                            <Select
                                              value={r.options?.[opt] ?? ""}
                                              onValueChange={(v) => updateManualQuoteValue(p.id, rowId, opt, v)}
                                            >
                                              <SelectTrigger
                                                className="h-9 bg-white/5 text-slate-100 ring-1 ring-white/10"
                                                data-testid={`appointment-quotes-dropdown-${panelId}-${rowId}-${opt}`}
                                              >
                                                <SelectValue
                                                  placeholder={
                                                    isIssueAge
                                                      ? profilesLoading
                                                        ? "Loading issue ages..."
                                                        : "Select issue age"
                                                      : "Select..."
                                                  }
                                                />
                                              </SelectTrigger>
                                              <SelectContent className="glass border-white/10">
                                                {(isCompany
                                                  ? companyOptions
                                                  : isProduct
                                                    ? getProductOptionsByCompanyName(companyRow?.options?.[opt] || "")
                                                    : isIssueAge
                                                      ? getIssueAgeOptions(companyRow?.options?.[opt] || "", productRow?.options?.[opt] || "")
                                                      : r.template_options || []
                                                )
                                                  .slice(0, 50)
                                                  .map((o, idx2) => (
                                                    <SelectItem
                                                      key={o}
                                                      value={o}
                                                      data-testid={`appointment-quotes-dropdown-option-${panelId}-${rowId}-${opt}-${idx2}`}
                                                    >
                                                      {o}
                                                    </SelectItem>
                                                  ))}
                                              </SelectContent>
                                            </Select>
                                          ) : (
                                            <Input
                                              type={inputType}
                                              value={r.options?.[opt] ?? ""}
                                              onChange={(e) => updateManualQuoteValue(p.id, rowId, opt, e.target.value)}
                                              className="h-9 bg-white/5 text-slate-100"
                                              data-testid={`appointment-quotes-input-${panelId}-${rowId}-${opt}`}
                                            />
                                          )
                                        ) : (
                                          r.options?.[opt] || "—"
                                        )
                                      ) : (
                                        r.options?.[opt] || "—"
                                      )}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
